import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import AlertModal, { AlertModalProps } from "@/components/molecules/AlertModal";
import { CART_VALIDATIONS_MESSAGES } from "@/config/messages";
import { useAppDispatch } from "@/hooks/application/store";
import { STORE_BY_ID_QUERY_KEY } from "@/hooks/domain/stores";
import { Address } from "@/interfaces/domain/Address";
import { OrderItem, OrderItemQualityEnum } from "@/interfaces/domain/Order";
import { Store } from "@/interfaces/domain/Store";
import navigationService from "@/navigation/navigationService";
import analytics from "@/services/analytics";
import { getStoreByIdRequest } from "@/services/api/store";
import { store } from "@/store";
import {
  ADD_ITEMS,
  CHANGE_ITEM_INSTRUCTION,
  CHANGE_ITEM_QUALITY,
  CHANGE_ITENS_NOT_FOUND,
  DECREASE_ITEM,
  INCREASE_ITEM,
  RESET_STORE_AND_ITENS,
  SET_STORE,
} from "@/store/ducks/checkout";
import { formatToCurrency } from "@/utils/helpers";

import { useViewport } from "./ViewportContext";

type CartControllerContextContextProps = {
  increaseItem: (item: OrderItem, quantity?: number, callback?: () => void) => void;
  decreaseItem: (item: OrderItem, quantity?: number) => void;
  addItems: (items: { item: OrderItem; quantity?: number }[], callback?: () => void) => void;
  clearCart: () => void;
  changeItemInstruction: (itemId: string, instruction: string) => void;
  changeItemQuality: (itemId: string, quality: OrderItemQualityEnum) => void;
  initializeCheckout: () => void;
  changeNotFoundItems: (text: string) => void;
};

const CartControllerContextContext = createContext<CartControllerContextContextProps>(null);

enum CartValidationType {
  NO_CLIENT_ADDRESS = "NO_CLIENT_ADDRESS",
  STORE_CLOSED = "STORE_CLOSED",
  OUT_OFF_DELIVERY_AREA = "OUT_OFF_DELIVERY_AREA",
  DIFF_STORES_IN_CART = "DIFF_STORES_IN_CART",
  MIN_ORDER_VALUE = "MIN_ORDER_VALUE",
  CLEAR_CART = "CLEAR_CART",
  ERROR_SHIPMENT_VALUE = "ERROR_SHIPMENT_VALUE",
}

type CartValidationsModal = Omit<AlertModalProps, "isOpen" | "onClose">;

export const CartControllerProvider = ({ children }) => {
  const { isWebMode } = useViewport();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [modalVisible, setModalVisible] = useState(false);

  const [activeModalProps, setActiveModalProps] = useState<CartValidationsModal>(
    {} as CartValidationsModal
  );

  const onCloseModal = () => {
    setModalVisible(false);
    setActiveModalProps({} as CartValidationsModal);
  };

  const handleClearCart = () => {
    analytics.logEvent("cart_cleaned");
    dispatch(RESET_STORE_AND_ITENS());
  };

  const dispatchAddToCartItemEvent = (item: OrderItem, store: Store) => {
    analytics.logEvent("added_to_cart", {
      product_id: item.item._id,
      product_name: item.item.nome,
      product_qtd: item.quantidade,
      product_price: item.item.preco.preco_encarte
        ? item.item.preco.preco_encarte
        : item.item.preco.preco_original,
      vendor_name: store.nome_fantasia,
    });
  };

  const modalProps: { [key: string]: CartValidationsModal } = useMemo(
    () => ({
      [CartValidationType.NO_CLIENT_ADDRESS]: {
        ...CART_VALIDATIONS_MESSAGES[CartValidationType.NO_CLIENT_ADDRESS],
        primaryButtonText: "Adicionar Endereço",
        enableSecondButton: true,
        onPressPrimaryButton: () => {
          onCloseModal();
          navigationService.navigate("root", { screen: "addressStack" });
        },
        onPressSecondButton: () => onCloseModal(),
      },
      [CartValidationType.STORE_CLOSED]: {
        ...CART_VALIDATIONS_MESSAGES[CartValidationType.STORE_CLOSED],
        onPressPrimaryButton: () => onCloseModal(),
      },
      [CartValidationType.OUT_OFF_DELIVERY_AREA]: {
        ...CART_VALIDATIONS_MESSAGES[CartValidationType.OUT_OFF_DELIVERY_AREA],
        onPressPrimaryButton: () => onCloseModal(),
      },
      [CartValidationType.DIFF_STORES_IN_CART]: {
        ...CART_VALIDATIONS_MESSAGES[CartValidationType.DIFF_STORES_IN_CART],
        primaryButtonText: "Limpar carrinho",
        enableSecondButton: true,
        onPressPrimaryButton: () => {
          handleClearCart();
          onCloseModal();
        },
        onPressSecondButton: () => onCloseModal(),
      },
      [CartValidationType.MIN_ORDER_VALUE]: {
        ...CART_VALIDATIONS_MESSAGES[CartValidationType.MIN_ORDER_VALUE],
        enableSecondButton: true,
        onPressPrimaryButton: () => onCloseModal(),
        onPressSecondButton: () => onCloseModal(),
      },
      [CartValidationType.CLEAR_CART]: {
        ...CART_VALIDATIONS_MESSAGES[CartValidationType.CLEAR_CART],
        primaryButtonText: "Limpar",
        enableSecondButton: true,
        onPressPrimaryButton: () => {
          handleClearCart();
          onCloseModal();
        },
        onPressSecondButton: () => onCloseModal(),
      },
      [CartValidationType.ERROR_SHIPMENT_VALUE]: {
        ...CART_VALIDATIONS_MESSAGES[CartValidationType.ERROR_SHIPMENT_VALUE],
        onPressPrimaryButton: () => onCloseModal(),
      },
    }),
    []
  );

  const validateWhenItemChange = (item: OrderItem, store: Store, address: Address) => {
    const storeOfItem = item.item?.estabelecimento as any;
    const storeId = (storeOfItem?._id || storeOfItem) as string;
    let props = null;
    if (store && store.status === "Fechado") {
      props = modalProps[CartValidationType.STORE_CLOSED];
    } else if (!address) {
      props = modalProps[CartValidationType.NO_CLIENT_ADDRESS];
    } else if (
      !store?.area_cobertura.find(
        (area) =>
          area.estado === address.estado &&
          area.bairro === address.bairro &&
          area.cidade === address.cidade
      )
    ) {
      props = modalProps[CartValidationType.OUT_OFF_DELIVERY_AREA];
    } else if (store && storeId !== store?._id) {
      props = modalProps[CartValidationType.DIFF_STORES_IN_CART];
    } else {
      return true;
    }
    setActiveModalProps(props);
    setModalVisible(true);
    return false;
  };

  const getStore = useCallback(async (item: OrderItem) => {
    const storeOfItem = item.item?.estabelecimento as any;
    const storeId = (storeOfItem?._id || storeOfItem) as string;
    const storedData = queryClient.getQueryData([STORE_BY_ID_QUERY_KEY(storeId)]);
    if (storedData) return storedData as Store;
    return queryClient.fetchQuery({
      queryKey: [STORE_BY_ID_QUERY_KEY(storeId)],
      queryFn: () => getStoreByIdRequest(storeId),
    });
  }, []);

  const increaseItem = useCallback(
    async (item: OrderItem, quantity?: number, callback = () => {}) => {
      const {
        checkout: { address, store: checkoutStore },
      } = store.getState();
      const currentStore = checkoutStore || (await getStore(item));
      if (validateWhenItemChange(item, currentStore, address)) {
        dispatchAddToCartItemEvent(item, currentStore);
        dispatch(INCREASE_ITEM({ item, quantity }));
        if (!checkoutStore) {
          dispatch(SET_STORE(currentStore));
        }
        callback();
      }
    },
    []
  );

  const decreaseItem = useCallback((item: OrderItem, quantity?: number) => {
    if (item.quantidade >= 0) {
      dispatch(DECREASE_ITEM({ item, quantity }));
    }
  }, []);

  const addItems = useCallback(
    async (items: { item: OrderItem; quantity?: number }[], callback = () => {}) => {
      const item = items[0]?.item;

      const {
        checkout: { address, store: checkoutStore },
      } = store.getState();
      const currentStore = checkoutStore || (await getStore(item));
      if (validateWhenItemChange(item, currentStore, address)) {
        items.map((item) => dispatchAddToCartItemEvent(item.item, currentStore));
        dispatch(ADD_ITEMS(items));
        if (!checkoutStore) {
          dispatch(SET_STORE(currentStore));
        }
        callback();
      }
    },
    []
  );

  const changeItemInstruction = useCallback((itemId: string, instruction: string) => {
    if (itemId) {
      dispatch(CHANGE_ITEM_INSTRUCTION({ itemId, instruction }));
    }
  }, []);

  const changeItemQuality = useCallback((itemId: string, quality: OrderItemQualityEnum) => {
    if (itemId && quality) {
      dispatch(CHANGE_ITEM_QUALITY({ itemId, quality }));
    }
  }, []);

  const initializeCheckout = useCallback(() => {
    const {
      checkout: { store: checkoutStore, subtotal, shipmentValue },
    } = store.getState();
    if (!checkoutStore) return;
    const minOrderValue = +checkoutStore.valor_minimo_pedido;
    const hasMinValue = subtotal >= minOrderValue;
    if (!hasMinValue) {
      const props = modalProps[CartValidationType.MIN_ORDER_VALUE];
      props.title = props.title.replace("{minOrderValue}", formatToCurrency(minOrderValue));
      const commonRoute = {
        screen: "homeStack",
        params: {
          screen: "storeHome",
          initial: false,
          params: {
            storeId: checkoutStore._id,
          },
        },
      };
      const navigationRoute = isWebMode
        ? commonRoute
        : {
            screen: "tabs",
            params: commonRoute,
          };
      props.onPressPrimaryButton = () => {
        setModalVisible(false);
        analytics.logEvent("checkout_initialized");
        navigationService.navigate("root", navigationRoute);
      };
      setActiveModalProps(props);
      setModalVisible(true);
      return null;
    }
    if (shipmentValue === null || shipmentValue === undefined) {
      const props = modalProps[CartValidationType.ERROR_SHIPMENT_VALUE];
      setActiveModalProps(props);
      setModalVisible(true);
      return null;
    }
    return navigationService.navigate("root", { screen: "checkout" });
  }, []);

  const clearCart = useCallback(() => {
    setActiveModalProps(modalProps[CartValidationType.CLEAR_CART]);
    setModalVisible(true);
  }, []);

  const changeNotFoundItems = useCallback((text: string) => {
    dispatch(CHANGE_ITENS_NOT_FOUND(text));
  }, []);

  const state = useMemo(
    () => ({
      increaseItem,
      decreaseItem,
      addItems,
      changeItemInstruction,
      changeItemQuality,
      clearCart,
      initializeCheckout,
      changeNotFoundItems,
    }),
    []
  );

  return (
    <CartControllerContextContext.Provider value={state}>
      {children}
      <AlertModal
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        {...activeModalProps}
      />
    </CartControllerContextContext.Provider>
  );
};

export const useCartController = () => useContext(CartControllerContextContext);
